import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import * as Sentry from "@sentry/solid"
import { useSearchParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import {
  createEffect,
  createMemo,
  createSignal,
  Match,
  onMount,
  Show,
  Switch,
} from "solid-js"
import z from "zod"
import { AuthFlow } from "@/components/auth-flow"
import { AuthLayout } from "@/components/auth-layout"
import { PrivacyDisclaimer } from "@/components/privacy-disclaimer"
import { createSession } from "@/context/session-provider"
import { http } from "@/data/http"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { FikaLogo } from "@/designSystem/components/fika-logo"
import { Spinner } from "@/designSystem/components/spinner"
import { Text } from "@/designSystem/components/text"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { headerImage, subscribeContainerClass } from "./subscribe.css"
import { SubscriptionConfirmation } from "./subscription-confirmation"

const subscribeSignupResponse = z.object({
  action: z.enum(["signed_up", "otp_sent"]),
})

const searchParamsSchema = z.object({
  publicationId: z.string(),
  postId: z.string().optional(),
  formId: z.string().optional(),
  returnTo: z.string().default(""),
  email: z.string().optional(),
  title: z.string().optional(),
  name: z.string().default(""),
})
type SearchParams = z.infer<typeof searchParamsSchema>

export function Subscribe() {
  const [searchParams] = useSearchParams()
  const parsedSearchParams = createMemo(() =>
    searchParamsSchema.safeParse(searchParams),
  )

  return (
    <Show
      when={parsedSearchParams().data}
      fallback={
        <ErrorMessage
          message={`error parsing search params ${parsedSearchParams().error?.message}`}
        />
      }
    >
      {params => <SubscribeWithSearchParams {...params()} />}
    </Show>
  )
}

function SubscribeWithSearchParams(props: SearchParams) {
  const { session } = createSession()

  return (
    <Switch
      fallback={
        <AuthLayout>
          <Box alignItems="center" padding="32" fill>
            <Spinner size="medium" />
          </Box>
        </AuthLayout>
      }
    >
      <Match when={session().status === "logged-out"}>
        <AuthLayout>
          <LoggedOutView
            email={props.email}
            title={props.title}
            name={props.name}
          />
        </AuthLayout>
      </Match>
      <Match when={session().status === "logged-in"}>
        <LoggedInView {...props} />
      </Match>
    </Switch>
  )
}

interface LoggedOutViewProps {
  email?: string
  title?: string
  name: string
}

function LoggedOutView(props: LoggedOutViewProps) {
  const { LL } = useI18nContext()
  const { actions } = createSession()
  const [frictionlessResult, setFrictionlessResult] = createSignal<
    "pending" | "otp" | "error"
  >("pending")

  let frictionlessStarted = false

  createEffect(async () => {
    if (!props.email || frictionlessStarted) return
    frictionlessStarted = true

    try {
      const res = await http("/api/subscribe-signup", {
        method: "POST",
        body: JSON.stringify({ email: props.email }),
        headers: { "Content-Type": "application/json" },
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = subscribeSignupResponse.parse(await res.json())
      if (data.action === "signed_up") {
        await actions.refetchSession()
      } else {
        setFrictionlessResult("otp")
      }
    } catch (err) {
      console.error("[subscribe] frictionless error:", err)
      setFrictionlessResult("error")
    }
  })

  return (
    <Show
      when={!props.email || frictionlessResult() !== "pending"}
      fallback={
        <Box alignItems="center" padding="32" fill>
          <Spinner size="medium" />
        </Box>
      }
    >
      <div class={subscribeContainerClass}>
        <Box flexDirection="column" gap="8" alignItems="center">
          <FikaLogo withText />
          <Text fontSize="h3" color="sand900" textAlign="center">
            {LL().auth.subscribeTo()} "
            {props.title ?? props.name ?? LL().auth.thisBlog()}"
          </Text>
          <Text
            fontSize="m"
            fontWeight="400"
            color="sand700"
            textAlign="center"
          >
            {LL().auth.getUpdates()}
          </Text>
        </Box>
        <AuthFlow
          initialEmail={props.email}
          initialStep={frictionlessResult() === "otp" ? "otp" : "email"}
          showSocialLogin={false}
          submitButtonLabel={LL().auth.subscribe()}
          signupSource="reader"
          frictionless
        />
        <PrivacyDisclaimer />
      </div>
    </Show>
  )
}

type SubscribeState = "idle" | "subscribing" | "done" | "error"

function LoggedInView(props: SearchParams) {
  const { LL } = useI18nContext()
  const [subscribeState, setSubscribeState] =
    createSignal<SubscribeState>("idle")

  const [dataResult] = createQuery({
    query: graphql(
      `query SubscribeData($publicationId: ID!) {
        publication(id: $publicationId) {
          id
          name
          title
          welcomeImageUrl
          logoUrl
          author {
            id
            authorName
          }
          sub {
            id
          }
        }
      }`,
    ),
    variables: () => ({ publicationId: props.publicationId }),
    requestPolicy: "network-only",
  })

  const publication = createMemo(() => dataResult.data?.publication)

  const publicationLogoUrl = createMemo(() => {
    const pub = publication()
    if (!pub) return null
    return pub.logoUrl ?? getDefaultAvatar(pub.id)
  })

  const SubscribeToMutation = graphql(
    `mutation SubscribeToFromPublic($targetUserId: String!, $postId: String, $formId: String) {
      subscribeTo(targetUserId: $targetUserId, postId: $postId, formId: $formId) {
        __typename
        ... on MutationSubscribeToSuccess { data { id } }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )

  const [_subscribeResult, subscribeMutation] = createMutationWithErrorHandling(
    SubscribeToMutation,
    "subscribeTo",
  )

  const [_claimResult, claimMutation] = createMutationWithErrorHandling(
    graphql(
      `mutation ClaimLeadMagnetFormFromSubscribe($formId: String!) {
        claimLeadMagnetForm(formId: $formId) {
          __typename
          ... on MutationClaimLeadMagnetFormSuccess { data }
          ...ErrorFragment
        }
      }`,
      [ErrorFragment],
    ),
    "claimLeadMagnetForm",
  )

  createEffect(async () => {
    const pub = publication()
    if (!pub || subscribeState() !== "idle") return

    if (pub.sub) {
      if (props.formId) {
        setSubscribeState("subscribing")
        const result = await claimMutation({ formId: props.formId })
        setSubscribeState(result.success ? "done" : "error")
        return
      }

      setSubscribeState("done")
      return
    }

    setSubscribeState("subscribing")

    const result = await subscribeMutation({
      targetUserId: props.publicationId,
      postId: props.postId ?? null,
      formId: props.formId ?? null,
    })
    setSubscribeState(result.success ? "done" : "error")
  })

  function handleSubscriptionSuccess() {
    window.location.href = props.returnTo || "/"
  }

  function handleRetry() {
    setSubscribeState("idle")
  }

  function header() {
    const url = publication()?.welcomeImageUrl
    if (!url) return undefined

    return (
      <img
        src={url}
        alt={props.title ?? props.name ?? LL().auth.thisBlog()}
        class={headerImage}
      />
    )
  }

  return (
    <AuthLayout header={header()}>
      <Switch
        fallback={
          <Box alignItems="center" padding="32" fill>
            <Spinner size="medium" />
          </Box>
        }
      >
        <Match when={subscribeState() === "done"}>
          <SubscriptionConfirmation
            publicationId={props.publicationId}
            publicationName={publication()?.name ?? ""}
            publicationLogoUrl={publicationLogoUrl()}
            authorName={publication()?.author?.authorName ?? undefined}
            onContinue={handleSubscriptionSuccess}
          />
        </Match>

        <Match when={subscribeState() === "error"}>
          <Box flexDirection="column" gap="16" alignItems="center" padding="32">
            <Text fontSize="m" color="rose600">
              {LL().auth.subscriptionError()}
            </Text>
            <Button hue="sand" kind="soft" onClick={handleRetry}>
              {LL().auth.subscribe()}
            </Button>
          </Box>
        </Match>

        <Match when={dataResult.fetching}>
          <Box alignItems="center" padding="32" fill>
            <Spinner size="medium" />
          </Box>
        </Match>

        <Match when={dataResult.error || !publication()}>
          <SubscribeError
            error={dataResult.error}
            publicationId={props.publicationId}
          />
        </Match>
      </Switch>
    </AuthLayout>
  )
}

function SubscribeError(props: {
  error: Error | undefined
  publicationId: string
}) {
  const { LL } = useI18nContext()

  onMount(() => {
    Sentry.captureException(
      props.error ??
        new Error(
          `Subscribe page: publication unavailable (id: ${props.publicationId})`,
        ),
    )
  })

  return (
    <Box flexDirection="column" gap="16" alignItems="center" padding="32">
      <Text fontSize="m" color="rose600">
        {LL().auth.somethingWentWrong()}
      </Text>
      <Show when={props.error}>
        {error => (
          <Text fontSize="s" color="sand600">
            {error().message}
          </Text>
        )}
      </Show>
    </Box>
  )
}

function ErrorMessage(props: { message: string }) {
  const { LL } = useI18nContext()
  return (
    <AuthLayout>
      <Box flexDirection="column" gap="16" alignItems="center">
        <Text fontSize="h3" fontWeight="500" color="rose600">
          {LL().auth.somethingWentWrong()}
        </Text>
        <Text fontSize="m" color="rose600" textAlign="center">
          {props.message}
        </Text>
      </Box>
    </AuthLayout>
  )
}
